/** Financial impact tab - ingredient cost, retail value, labour cost, disposal cost, avoidable
 * waste cost, savings opportunity, budget vs actual, and cost per site / cost per cover. */

import { Box, Grid, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../components/ui/Section';
import { applyRoundAndFormat } from '../../../utils';
import { FINANCIAL_IMPACT } from '../data';
import { StatusChip } from './StatusChip';

interface FinancialTabProps {
  currencyCode?: string;
}

const FinancialTab = ({ currencyCode }: FinancialTabProps) => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const f = FINANCIAL_IMPACT;
  const budgetVariance = f.actualWasteCost - f.budgetWasteCost;
  const budgetVariancePercent = (budgetVariance / f.budgetWasteCost) * 100;

  const tiles = [
    { key: 'ingredientCostLost', label: t('wasteDashboardIngredientCostLost', 'Ingredient cost lost'), value: f.ingredientCostLost },
    { key: 'retailValueLost', label: t('wasteDashboardRetailValueLost', 'Retail / sales value lost'), value: f.retailValueLost },
    { key: 'labourCostOfWastedPrep', label: t('wasteDashboardLabourCostLost', 'Labour cost of wasted preparation'), value: f.labourCostOfWastedPrep },
    { key: 'disposalCost', label: t('wasteDashboardDisposalCost', 'Disposal cost'), value: f.disposalCost },
    { key: 'totalAvoidableWasteCost', label: t('wasteDashboardTotalAvoidableCost', 'Total avoidable waste cost'), value: f.totalAvoidableWasteCost },
    { key: 'estimatedSavingsOpportunity', label: t('wasteDashboardSavingsOpportunity', 'Estimated savings opportunity'), value: f.estimatedSavingsOpportunity },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textMD"
            fontWeight={600}
            sx={{ mb: 3 }}>
            {t('wasteDashboardFinancialTitle', 'Financial impact')}
          </Typography>
          <Grid container
            spacing={4}>
            {tiles.map((tile) => (
              <Grid item
                xs={6}
                md={4}
                key={tile.key}>
                <Typography variant="textXS"
                  color="text.secondary">{tile.label}</Typography>
                <Typography variant="textLG"
                  fontWeight={600}>{applyRoundAndFormat(tile.value, currencyCode)}</Typography>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Section>

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textMD"
            fontWeight={600}
            sx={{ mb: 3 }}>
            {t('wasteDashboardBudgetVsActual', 'Budget vs actual waste cost')}
          </Typography>
          <Grid container
            spacing={4}
            alignItems="center">
            <Grid item
              xs={6}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('wasteDashboardBudget', 'Budget')}</Typography>
              <Typography variant="textLG"
                fontWeight={600}>{applyRoundAndFormat(f.budgetWasteCost, currencyCode)}</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('wasteDashboardActual', 'Actual')}</Typography>
              <Typography variant="textLG"
                fontWeight={600}>{applyRoundAndFormat(f.actualWasteCost, currencyCode)}</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('wasteDashboardVariance', 'Variance')}</Typography>
              <StatusChip status={budgetVariance > 0 ? 'critical' : 'good'}
                label={`${budgetVariance > 0 ? '+' : ''}${applyRoundAndFormat(budgetVariance, currencyCode)} (${budgetVariancePercent.toFixed(1)}%)`} />
            </Grid>
          </Grid>
          <Box sx={{ mt: 3, width: '100%', height: 12, borderRadius: '6px', backgroundColor: theme.palette.custom.gray[200], overflow: 'hidden' }}>
            <Box sx={{
              width: `${Math.min(100, (f.budgetWasteCost / f.actualWasteCost) * 100)}%`,
              height: '100%',
              backgroundColor: theme.palette.primary.main,
            }} />
          </Box>
        </Box>
      </Section>

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textMD"
            fontWeight={600}
            sx={{ mb: 3 }}>
            {t('wasteDashboardCostPerSiteCover', 'Cost per site and cost per cover')}
          </Typography>
          <Grid container
            spacing={4}>
            <Grid item
              xs={6}
              md={4}>
              <Typography variant="textXS"
                color="text.secondary">{t('wasteDashboardCostPerSite', 'Cost per site (avg)')}</Typography>
              <Typography variant="textLG"
                fontWeight={600}>{applyRoundAndFormat(f.costPerSite, currencyCode)}</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={4}>
              <Typography variant="textXS"
                color="text.secondary">{t('wasteDashboardCostPerCover', 'Cost per cover')}</Typography>
              <Typography variant="textLG"
                fontWeight={600}>{applyRoundAndFormat(f.costPerCover, currencyCode)}</Typography>
            </Grid>
          </Grid>
        </Box>
      </Section>
    </Box>
  );
};

export default FinancialTab;
